import {searchUrl} from '@github-ui/issue-url-helper'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import {getMostUsedLabels} from '@github-ui/item-picker-label-tsq/favorite-labels-storage'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {TagIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import {LABELS} from '../../../constants/labels'
import {useActiveSearchQuery, useCurrentViewId, useQueryDebouncedSearch} from '../../../contexts/QueryContext'
import {getTokensByType, replaceTokensDifferentially} from '../../../utils/urls'
import type {FilterBarPickerProps} from './ListItemsHeaderWithoutBulkActions'
import styles from './ListLabelFilter.module.css'

export function ListLabelFilter({nested, repo, applySectionFilter}: FilterBarPickerProps) {
  const currentViewId = useCurrentViewId()
  const activeSearchQuery = useActiveSearchQuery()
  const {debouncedDirtySearchQuery} = useQueryDebouncedSearch()

  const query = debouncedDirtySearchQuery ?? activeSearchQuery

  const NoLabelItem = useMemo(() => {
    const option: ExtendedItemProps<LabelPickerLabel> = {
      id: SPECIAL_VALUES.noLabelsData.id,
      description: '',
      descriptionVariant: 'inline',
      children: (
        <VerifiedHTMLText
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
          html={SPECIAL_VALUES.noLabelsData.name as SafeHTMLString}
          title={SPECIAL_VALUES.noLabelsData.description}
        />
      ),
      source: SPECIAL_VALUES.noLabelsData as LabelPickerLabel,
      selected: query.includes('no:label'),
      groupId: '',
      leadingVisual: () => <div className={styles.labelColorIndicator} />,
    }
    return option
  }, [query])

  const onSelectionChanged = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const labels = selectedLabels.map(label => {
        if (label.id === SPECIAL_VALUES.noLabelsData.id) {
          return 'no:label'
        }
        return label.name
      })
      const newQuery = replaceTokensDifferentially(query, labels, 'label')
      const url = searchUrl({viewId: currentViewId, query: newQuery})
      applySectionFilter(newQuery, url)
    },
    [query, applySectionFilter, currentViewId],
  )

  const currentLabelTokens = useMemo(() => {
    const tokens = getTokensByType(query, 'label')
    return tokens.length > 0 ? tokens : undefined
  }, [query])

  // Surface the viewer's most-used labels in the suggestions group. Do not add a
  // recordLabelsUsage call here — filtering a list must not affect usage ranking.
  const favoriteLabelIds = useMemo(
    () => getMostUsedLabels(`${repo.owner}/${repo.name}`).map(entry => entry.labelId),
    [repo.owner, repo.name],
  )

  return (
    <LabelPicker
      anchorElement={nested ? NestedLabelsAnchor : LabelsAnchor}
      title={LABELS.filters.labelsLabel}
      showEditLabelsButton={false}
      repo={repo.name}
      owner={repo.owner}
      readonly={false}
      onSelectionChange={onSelectionChanged}
      noLabelOption={NoLabelItem}
      labels={[]}
      labelNames={currentLabelTokens}
      canCreateLabel={false}
      enableSuggestedLabels
      favoriteLabelIds={favoriteLabelIds}
    />
  )
}

function NestedLabelsAnchor(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <ActionList.Item {...props} aria-label={LABELS.filters.labelsLabel} role="menuitem">
      <ActionList.LeadingVisual>
        <TagIcon />
      </ActionList.LeadingVisual>
      {LABELS.filters.labels}...
    </ActionList.Item>
  )
}

function LabelsAnchor(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      variant="invisible"
      data-testid="labels-anchor-button"
      trailingVisual={TriangleDownIcon}
      aria-label={LABELS.filters.labelsLabel}
      className={styles.labelFilterButton}
      {...props}
    >
      {LABELS.filters.labels}
    </Button>
  )
}
